import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import IntegrationSidebar from "../components/integration/IntegrationSidebar";
import IntegrationContent from "../components/integration/IntegrationContent";
import {
    Checklist,
    Settings,
    VpnKey,
    CreditCard,
    NotificationsActive,
    Star
} from '@mui/icons-material';

const Integration = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const stepParam = searchParams.get('step');
    const [activeStep, setActiveStep] = useState(stepParam || 'prerequisites');

    const integrationSteps = useMemo(() => [
        {
            id: 'prerequisites',
            title: 'Prérequis',
            Icon: Checklist,
            duration: '5 min'
        },
        {
            id: 'account-setup',
            title: 'Configuration du Compte',
            Icon: Settings,
            duration: '10 min'
        },
        {
            id: 'authentication',
            title: 'Authentification',
            Icon: VpnKey,
            duration: '15 min'
        },
        {
            id: 'payment-flow',
            title: 'Flux de Paiement',
            Icon: CreditCard,
            duration: '20 min'
        },
        {
            id: 'webhooks',
            title: 'Webhooks',
            Icon: NotificationsActive,
            duration: '15 min'
        },
        {
            id: 'best-practices',
            title: 'Best Practices',
            Icon: Star,
            duration: '10 min'
        }
    ], []);


    // Mettre à jour l'URL quand le step change
    const handleStepChange = (stepId) => {
        setActiveStep(stepId);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('step', stepId);
        setSearchParams(newParams, { replace: true });
    };

    useEffect(() => {
        if (stepParam) {
            const found = integrationSteps.find(s => s.id === stepParam);
            if (found) setActiveStep(stepParam);
        }
    }, [stepParam, integrationSteps]);

    const activeStepData = integrationSteps.find(step => step.id === activeStep);
    const activeIndex = integrationSteps.findIndex(step => step.id === activeStep);

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pt-16 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors">
            {/* Header Hero */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                            <span className="w-3 h-3 bg-emerald-400 rounded-full mr-3 animate-pulse"></span>
                            <span className="text-white/90 font-semibold text-lg">GUIDE D'INTÉGRATION</span>
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                            Intégrez Netikash en{' '}
                            <span className="bg-gradient-to-r from-amber-200 to-yellow-200 bg-clip-text text-transparent">
                                15 minutes
                            </span>
                        </h1>
                        <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
                            Suivez ce guide étape par étape pour intégrer les paiements Netikash
                            dans votre application rapidement et efficacement.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Navigation */}
                    <div className="lg:w-80 flex-shrink-0">
                        <IntegrationSidebar
                            steps={integrationSteps}
                            activeStep={activeStep}
                            activeIndex={activeIndex}
                            onStepChange={handleStepChange}
                        />
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                        <div className="bg-white rounded-2xl shadow-lg border border-amber-100/50 overflow-hidden">
                            <IntegrationContent step={activeStepData} activeIndex={activeIndex} totalSteps={integrationSteps.length} />
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between items-center mt-8">
                            <button
                                onClick={() => {
                                    const currentIndex = integrationSteps.findIndex(step => step.id === activeStep);
                                    if (currentIndex > 0) {
                                        handleStepChange(integrationSteps[currentIndex - 1].id);
                                    }
                                }}
                                className="flex items-center space-x-2 px-6 py-3 rounded-lg border border-amber-300 text-amber-700 font-medium hover:bg-amber-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={activeStep === 'prerequisites'}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span>Étape précédente</span>
                            </button>

                            <button
                                onClick={() => {
                                    const currentIndex = integrationSteps.findIndex(step => step.id === activeStep);
                                    if (currentIndex < integrationSteps.length - 1) {
                                        handleStepChange(integrationSteps[currentIndex + 1].id);
                                    }
                                }}
                                className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-medium hover:from-amber-600 hover:to-yellow-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={activeStep === 'best-practices'}
                            >
                                <span>Étape suivante</span>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Integration;